(() => {
  if (window.lfCatalogueReady) return;
  window.lfCatalogueReady = true;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prepared = new WeakSet();
  let requestController;
  let requestVersion = 0;
  let renderedURL = new URL(location.href);
  const root = () => document.querySelector('[data-catalogue]');
  const anchors = nav => [...nav.querySelectorAll('[data-catalog-link]')];
  const activeLink = nav => nav.querySelector('[aria-current="page"]') || anchors(nav)[0];
  const placePill = (nav, link) => {
    if (!link) return;
    const bounds = nav.getBoundingClientRect();
    const target = link.getBoundingClientRect();
    nav.style.setProperty('--pill-x', `${target.left - bounds.left - nav.clientLeft}px`);
    nav.style.setProperty('--pill-width', `${target.width}px`);
    nav.style.setProperty('--pill-height', `${target.height}px`);
    nav.style.setProperty('--pill-y', `${target.top - bounds.top - nav.clientTop}px`);
  };
  const readyNav = nav => {
    if (prepared.has(nav)) { placePill(nav, activeLink(nav)); return; }
    prepared.add(nav);
    nav.classList.add('is-interactive');
    placePill(nav, activeLink(nav));
    let drag;
    let suppressClick = false;
    const nearest = x => anchors(nav).reduce((best, link) => {
      const box = link.getBoundingClientRect();
      const distance = Math.abs(x - (box.left + box.width / 2));
      return !best || distance < best.distance ? { link, distance } : best;
    }, null)?.link;
    const reset = () => {
      nav.classList.remove('is-dragging');
      anchors(nav).forEach(link => {
        link.style.removeProperty('--tab-text-x');
        link.style.removeProperty('--tab-text-y');
      });
      placePill(nav, activeLink(nav));
    };
    nav.addEventListener('pointerdown', event => {
      if (event.button !== 0 || event.isPrimary === false || !event.target.closest('[data-catalog-link]')) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    });
    nav.addEventListener('pointermove', event => {
      const bounds = nav.getBoundingClientRect();
      nav.style.setProperty('--glass-x', `${event.clientX - bounds.left}px`);
      nav.style.setProperty('--glass-y', `${event.clientY - bounds.top}px`);
      if (drag && event.pointerId === drag.id) {
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.moved && Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) { drag = null; reset(); return; }
        if (!drag.moved && Math.abs(dx) > 7) {
          drag.moved = true;
          nav.classList.add('is-dragging');
          nav.setPointerCapture?.(event.pointerId);
        }
        if (drag.moved) {
          const link = nearest(event.clientX);
          placePill(nav, link);
          const width = link.getBoundingClientRect().width;
          const left = Math.max(5, Math.min(bounds.width - width - 5, event.clientX - bounds.left - width / 2));
          nav.style.setProperty('--pill-x', `${left}px`);
          event.preventDefault();
          return;
        }
      }
      if (event.pointerType === 'touch') return;
      const link = event.target.closest('[data-catalog-link]');
      if (!link) return;
      placePill(nav, link);
      if (!reducedMotion.matches) {
        const box = link.getBoundingClientRect();
        link.style.setProperty('--tab-text-x', `${(event.clientX - box.left - box.width / 2) * 0.035}px`);
        link.style.setProperty('--tab-text-y', `${(event.clientY - box.top - box.height / 2) * 0.07}px`);
      }
    });
    nav.addEventListener('pointerup', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const moved = drag.moved;
      drag = null;
      nav.classList.remove('is-dragging');
      if (nav.hasPointerCapture?.(event.pointerId)) nav.releasePointerCapture(event.pointerId);
      if (moved) {
        suppressClick = true;
        const target = nearest(event.clientX);
        if (target) { target.focus({ preventScroll: true }); navigate(target.href, { format: target.dataset.format }); }
        window.setTimeout(() => { suppressClick = false; }, 300);
      } else reset();
    });
    nav.addEventListener('click', event => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClick = false;
    }, true);
    nav.addEventListener('pointercancel', () => { drag = null; reset(); });
    nav.addEventListener('pointerleave', () => { if (!drag?.moved) reset(); });
    nav.addEventListener('focusin', event => placePill(nav, event.target.closest('[data-catalog-link]')));
    nav.addEventListener('focusout', event => { if (!nav.contains(event.relatedTarget)) reset(); });
    nav.addEventListener('keydown', event => {
      const links = anchors(nav);
      const index = links.indexOf(event.target);
      if (index < 0 || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      let next = event.key === 'Home' ? 0 : event.key === 'End' ? links.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + links.length) % links.length;
      links[next].focus();
    });
    if ('ResizeObserver' in window) new ResizeObserver(() => placePill(nav, activeLink(nav))).observe(nav);
    else window.addEventListener('resize', () => placePill(nav, activeLink(nav)), { passive: true });
  };
  const enhance = () => {
    const catalog = root();
    if (!catalog) return;
    catalog.classList.add('is-enhanced');
    catalog.querySelectorAll('[data-catalog-tabs]').forEach(readyNav);
    catalog.querySelectorAll('.pagination a').forEach(link => { link.dataset.noTransition = ''; });
  };
  const previewSort = (catalog, url) => {
    if (!/\.html$/.test(url.pathname) && !document.body.hasAttribute('data-preview')) return;
    const grid = catalog.querySelector('.product-grid');
    const sort = url.searchParams.get('sort_by');
    if (grid && sort) {
      const cards = [...grid.children];
      const price = card => Number((card.querySelector('.card-price')?.textContent || '').replace(/[^\d,.]/g, '').replace(',', '.')) || 0;
      const title = card => card.querySelector('.card-heading')?.textContent.trim() || '';
      if (sort === 'title-ascending' || sort === 'title-descending') cards.sort((a, b) => title(a).localeCompare(title(b), document.documentElement.lang) * (sort.endsWith('ascending') ? 1 : -1));
      if (sort === 'price-ascending' || sort === 'price-descending') cards.sort((a, b) => (price(a) - price(b)) * (sort.endsWith('ascending') ? 1 : -1));
      grid.replaceChildren(...cards);
    }
    const select = catalog.querySelector('[data-catalogue-sort-select]');
    if (select && sort && [...select.options].some(option => option.value === sort)) select.value = sort;
  };
  async function navigate(href, { historyMode = 'push', format, focusSort = false, focusResults = false } = {}) {
    const catalog = root();
    if (!catalog) return;
    const url = new URL(href, location.href);
    if (url.origin !== location.origin) return;
    url.searchParams.delete('section_id');
    const currentURL = new URL(renderedURL);
    if (format) {
      const sortSelect = catalog.querySelector('[data-catalogue-sort-select]');
      const currentSort = sortSelect?.value;
      const renderedSort = [...(sortSelect?.options || [])].find(option => option.defaultSelected)?.value || sortSelect?.options[0]?.value;
      if (currentSort && (currentURL.searchParams.has('sort_by') || currentSort !== renderedSort)) url.searchParams.set('sort_by', currentSort);
      else url.searchParams.delete('sort_by');
      url.searchParams.delete('page');
    }
    if (url.href === renderedURL.href && historyMode !== 'pop') {
      requestVersion++;
      requestController?.abort();
      catalog.classList.remove('is-loading', 'is-error');
      catalog.querySelector('[data-catalogue-results]')?.removeAttribute('aria-busy');
      catalog.querySelector('[data-catalogue-status]').textContent = '';
      const nav = catalog.querySelector('[data-catalog-tabs]');
      placePill(nav, activeLink(nav));
      return;
    }
    requestController?.abort();
    const version = ++requestVersion;
    const controller = new AbortController();
    requestController = controller;
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    const status = catalog.querySelector('[data-catalogue-status]');
    const retry = catalog.querySelector('[data-catalogue-retry]');
    status.textContent = catalog.dataset.loadingText;
    retry.hidden = true;
    catalog.querySelector('[data-catalogue-results]').setAttribute('aria-busy', 'true');
    catalog.classList.remove('is-error');
    catalog.classList.add('is-loading');
    const nav = catalog.querySelector('[data-catalog-tabs]');
    const requestedLink = format && nav.querySelector(`[data-format="${format}"]`);
    if (requestedLink) placePill(nav, requestedLink);
    const fetchURL = new URL(url);
    const sameTemplate = currentURL.pathname === url.pathname && currentURL.searchParams.get('view') === url.searchParams.get('view');
    const isPreview = /\.html$/.test(url.pathname) || document.body.hasAttribute('data-preview');
    if (sameTemplate && !isPreview) fetchURL.searchParams.set('section_id', catalog.dataset.sectionId);
    try {
      const response = await fetch(fetchURL.href, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('catalogue-response');
      const html = await response.text();
      if (version !== requestVersion) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.querySelector('[data-catalogue]');
      const incomingResults = incoming?.querySelector('[data-catalogue-results]');
      if (!incomingResults) throw new Error('catalogue-content');
      previewSort(incoming, url);
      const oldFocus = document.activeElement;
      const hadSortFocus = focusSort || oldFocus?.matches('[data-catalogue-sort-select]');
      catalog.querySelector('[data-catalogue-results]').replaceWith(incomingResults);
      catalog.dataset.sectionId = incoming.dataset.sectionId;
      catalog.dataset.format = incoming.dataset.format;
      const incomingTabs = incoming.querySelector('[data-catalog-tabs]');
      anchors(nav).forEach(link => {
        const source = incomingTabs?.querySelector(`[data-format="${link.dataset.format}"]`);
        if (source) link.href = source.href;
        const destination = new URL(link.href, location.href);
        if (url.searchParams.has('sort_by')) destination.searchParams.set('sort_by', url.searchParams.get('sort_by'));
        link.href = destination.href;
        if (link.dataset.format === incoming.dataset.format) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      const title = parsed.querySelector('title')?.textContent;
      if (title) document.title = title;
      renderedURL = new URL(url);
      if (historyMode === 'push') history.pushState({ ...history.state, lfCatalogue: true }, '', url);
      if (hadSortFocus) catalog.querySelector('[data-catalogue-sort-select]')?.focus({ preventScroll: true });
      else if (focusResults) incomingResults.focus({ preventScroll: true });
      enhance();
      document.dispatchEvent(new CustomEvent('lf:section:updated', { detail: { root: incomingResults } }));
      const count = incomingResults.querySelectorAll('.product-card').length;
      status.textContent = catalog.dataset.loadedText.replace('__count__', count);
    } catch (error) {
      if (version !== requestVersion) return;
      catalog.classList.add('is-error');
      status.textContent = catalog.dataset.errorText;
      retry.querySelector('a').href = url.href;
      retry.hidden = false;
      placePill(nav, activeLink(nav));
    } finally {
      window.clearTimeout(timeout);
      if (version === requestVersion) {
        catalog.querySelector('[data-catalogue-results]')?.removeAttribute('aria-busy');
        catalog.classList.remove('is-loading');
      }
    }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-catalogue] [data-catalog-link], [data-catalogue] .pagination a');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank' || link.hasAttribute('download')) return;
    if (new URL(link.href, location.href).origin !== location.origin) return;
    event.preventDefault();
    navigate(link.href, { format: link.dataset.format, focusResults: !!link.closest('.pagination') });
  });
  const sortForm = form => {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    new FormData(form).forEach((value, key) => url.searchParams.set(key, value));
    if (/\.html$/.test(url.pathname)) url.searchParams.delete('view');
    navigate(url.href, { focusSort: true });
  };
  document.addEventListener('submit', event => {
    if (!event.target.matches('[data-catalogue-sort]')) return;
    event.preventDefault();
    sortForm(event.target);
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-catalogue-sort-select]')) sortForm(event.target.form);
  });
  window.addEventListener('popstate', event => {
    if (root() && event.state?.lfCatalogue) navigate(location.href, { historyMode: 'pop' });
  });
  document.addEventListener('shopify:section:load', enhance);
  if (root()) previewSort(root(), new URL(location.href));
  enhance();
  if (root()) history.replaceState({ ...history.state, lfCatalogue: true }, '', location.href);
})();
